import React from "react";
import { Tooltip } from "antd";
import { HelpCircle } from "lucide-react";

const MBTIAnalysis = ({ data }) => {
  // Fallback if no data
  if (!data || !data.dimensionScores) {
    return (
      <div className="p-6 bg-[#D4E7FD] rounded-xl shadow border-[1px] border-[#A9CFFB]">
        <h1 className="text-black text-[1.275rem] font-semibold mb-3">
          MBTI Analysis (Myers-Briggs Type Indicator)
        </h1>{" "}
        <div className="bg-white p-6 rounded-xl border-[1px] shadow text-center text-gray-500">
          MBTI Data not found.
        </div>
      </div>
    );
  }

  const safe = (val, fallback = "N/A") => val ?? fallback;

  // ✅ Help content (verified links)
  const sectionHelp = {
    "Extroversion (E) vs Introversion (I)": {
      text: "This dimension shows where you focus your energy: outward toward people and activity (Extraversion) vs inward toward thoughts and reflection (Introversion).",
      link: "https://www.myersbriggs.org/my-mbti-personality-type/the-mbti-preferences/",
    },
    "Sensing (S) or Intuition (N)": {
      text: "This dimension reflects how you process information: through concrete facts and details (Sensing) vs patterns and possibilities (Intuition).",
      link: "https://www.myersbriggs.org/my-mbti-personality-type/the-mbti-preferences/",
    },
    "Thinking (T) or Feeling (F)": {
      text: "This dimension explains decision-making: logic and objectivity (Thinking) vs values and emotions (Feeling).",
      link: "https://www.myersbriggs.org/my-mbti-personality-type/the-mbti-preferences/",
    },
    "Judging (J) or Perceiving (P)": {
      text: "This dimension shows your approach to structure: planning and organization (Judging) vs flexibility and spontaneity (Perceiving).",
      link: "https://www.myersbriggs.org/my-mbti-personality-type/the-mbti-preferences/",
    },
    "Final MBTI Type": {
      text: "The MBTI type combines all the four dimensions into a 4-letter personality type (e.g., INFP, ESTJ), summarizing the candidate's preferences.",
      link: "https://hireplusplus-public-in.s3.ap-south-1.amazonaws.com/MBTI.svg",
    },
  };

  // ✅ Reusable Help Icon with Tooltip
  const HelpIcon = ({ title, link, type }) => (
    <Tooltip
      placement="top"
      title={
        <div className="max-w-sm text-[.84rem]">
          <p className="">
            {title}
            {type === "Final MBTI Type" ? (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 font-medium underline ml-1"
              >
                View MBTI types
              </a>
            ) : (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 font-medium underline ml-1"
              >
                Learn more
              </a>
            )}
          </p>
        </div>
      }
    >
      <span className="ml-2 cursor-pointer inline-flex">
        <HelpCircle size={18} className="text-gray-600 hover:text-blue-500" />
      </span>
    </Tooltip>
  );

  const dimensions = [
    {
      title: "Extroversion (E) vs Introversion (I)",
      data: data.dimensionScores.extroversionVsIntroversion,
    },
    {
      title: "Sensing (S) or Intuition (N)",
      data: data.dimensionScores.sensingVsIntuition,
    },
    {
      title: "Thinking (T) or Feeling (F)",
      data: data.dimensionScores.thinkingVsFeeling,
    },
    {
      title: "Judging (J) or Perceiving (P)",
      data: data.dimensionScores.judgingVsPerceiving,
    },
  ];

  const Card = ({ title, item }) => {
    if (!item) {
      return (
        <div className="bg-white rounded-2xl shadow p-5 text-gray-400">
          <h2 className="font-semibold mb-2 flex items-center">
            {title}
            <HelpIcon
              title={sectionHelp[title]?.text}
              link={sectionHelp[title]?.link}
            />
          </h2>
          Data not available
        </div>
      );
    }

    const scoreAKey = Object.keys(item).find(
      (key) =>
        key.toLowerCase().includes("score") &&
        !key.toLowerCase().includes("introversion") &&
        !key.toLowerCase().includes("intuition") &&
        !key.toLowerCase().includes("feeling") &&
        !key.toLowerCase().includes("perceiving"),
    );

    const scoreBKey = Object.keys(item).find(
      (key) =>
        key.toLowerCase().includes("score") &&
        !key
          .toLowerCase()
          .includes(scoreAKey?.replace("Score", "").toLowerCase()),
    );

    const capitalize = (str) =>
      str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

    return (
      <div className="bg-white text-[.9rem] text-[#475467] rounded-xl shadow-md p-5 border hover:scale-[1.023] transition-all duration-300">
        {/* Title + Help */}
        <h2 className="text-lg text-black font-semibold mb-2 flex items-center">
          {title}
          <HelpIcon
            title={sectionHelp[title]?.text}
            link={sectionHelp[title]?.link}
          />
        </h2>

        {/* Scores */}
        <div className="flex gap-2 text-sm mb-2 font-medium">
          <span>
            {scoreAKey
              ? `${capitalize(scoreAKey.replace("Score", ""))} Score: ${safe(item[scoreAKey])}`
              : "N/A"}
            ,
          </span>

          <span>
            {scoreBKey
              ? `${capitalize(scoreBKey.replace("Score", ""))} Score: ${safe(item[scoreBKey])}`
              : "N/A"}
          </span>
        </div>

        {/* Leaning */}
        <div className="mb-1">
          <span className="font-semibold">Leaning: </span>
          <span className="text-[#206DC5] font-semibold">
            {safe(item?.leaning)}
          </span>
        </div>

        {/* Confidence */}
        <div className="mb-1">
          <span className="font-semibold">Confidence: </span>
          <span
            className={`${
              item?.confidence === "High"
                ? "text-green-500"
                : item?.confidence === "Low"
                  ? "text-red-500"
                  : "text-[#206DC5]"
            } font-semibold`}
          >
            {safe(item?.confidence)}
          </span>
        </div>

        {/* Keywords */}
        <div className="mb-2">
          <span className="font-semibold">Keywords:</span>

          <div className="flex flex-wrap gap-2 mt-1">
            {(item.keywords ?? []).length > 0 ? (
              item.keywords.map((k, i) => (
                <span
                  key={i}
                  className="px-2 py-1 text-xs font-medium rounded-[8px] shadow-sm
                     bg-[#F2F8FE] text-[#206DC5] border-[1px] border-[#D4E7FD]
                     hover:bg-blue-100 transition"
                >
                  {k}
                </span>
              ))
            ) : (
              <span className="text-gray-400 text-sm">No keywords</span>
            )}
          </div>
        </div>

        {/* <div className="mb-2">
          <span className="font-semibold">Keywords:</span>
          <ul className="list-disc list-inside text-sm mt-1">
            {(item.keywords ?? []).length > 0 ? (
              item.keywords.map((k, i) => <li key={i}>{k}</li>)
            ) : (
              <li className="text-gray-400">No keywords</li>
            )}
          </ul>
        </div> */}

        {/* Reasoning */}
        <p className="text-sm text-gray-600 mt-2">
          <span className="font-semibold">Reasoning: </span>
          {safe(item?.reasoning, "No reasoning provided")}
        </p>
      </div>
    );
  };

  return (
    <div className="p-6 bg-[#D4E7FD] rounded-xl shadow border-[1px] border-[#A9CFFB] min-h-screen">
      {/* Title */}
      <h1 className="text-black text-[1.275rem] font-semibold mb-[.6rem]">
        MBTI (Myers-Briggs Type Indicator) Analysis
      </h1>

      {/* Note */}
      <p className="text-[.9rem] leading-[1.18rem] text-[#475467] font-normal mb-5">
        This section presents a personality-based analysis derived from the
        candidate's interview responses using the Myers-Briggs Type Indicator
        (MBTI) framework. It highlights how the candidate is likely to approach
        communication, decision-making, problem-solving, and work structure.
        These insights can support in evaluating cultural fit, team
        compatibility, and role alignment.
        <a
          href="https://en.wikipedia.org/wiki/Myers%E2%80%93Briggs_Type_Indicator"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-500 font-medium underline ml-1"
        >
          Learn how MBTI works
        </a>
      </p>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {dimensions.map((dim, index) => (
          <Card key={index} title={dim?.title} item={dim?.data} />
        ))}
      </div>

      {/* Final Summary */}
      <div className="bg-white rounded-xl border-[1px] border-[#A9CFFB] p-6 hover:scale-[1.023] transition-all duration-300">
        <h2 className="text-xl font-bold mb-2 flex items-center">
          Final MBTI Type:{" "}
          <span className="text-[#206DC5] ml-2">
            {" "}
            {safe(data?.finalMbtiType)}
          </span>
          <HelpIcon
            title={sectionHelp["Final MBTI Type"]?.text}
            link={sectionHelp["Final MBTI Type"]?.link}
            type="Final MBTI Type"
          />
        </h2>

        <p className="mb-2">
          <span className="font-semibold text-[#475467]">Confidence: </span>
          <span
            className={`${
              data?.overallMbtiConfidence === "High"
                ? "text-green-500"
                : data?.overallMbtiConfidence === "Low"
                  ? "text-red-500"
                  : data?.overallMbtiConfidence === "Medium"
                    ? "text-yellow-500"
                    : "text-[#206DC5]"
            } font-semibold`}
          >
            {safe(data?.overallMbtiConfidence)}
          </span>
        </p>

        <p className="text-sm text-gray-600">
          {safe(data?.mbtiSummary, "No summary available")}
        </p>
      </div>
      <p class="text-[.84rem] text-gray-500/95 font-light mt-3 text-center">
        <span className="">Note:</span> This analysis is AI-generated and may not be fully accurate.
        Please interpret it with appropriate discretion.
      </p>
    </div>
  );
};

export default MBTIAnalysis;
